/**
 * features/exams/ExamRegistration.tsx
 * ------------------------------------------------------------------
 * Exams → Registration (tab `examRegistration`, gate canManageExamTimetables:
 * the exams office). 3 Nov 2026, asked for by the Examinations Unit: the list
 * of candidates for a Cambridge sitting, with the details Cambridge needs, as
 * the .csv that Direct's `Add Candidates from Spreadsheet' screen takes.
 *
 * Pick the sitting, tick the classes, take off anyone not sitting. Each pupil's
 * details come from their record, and the table says what is wrong with each,
 * so a fix is made once on the record rather than in a spreadsheet. Only what
 * exists nowhere else is typed here: candidate numbers, previous entry details,
 * and a name or first-language answer where the record will not do. That is
 * saved per sitting in `reportExamRegistrations/{sitting}`.
 *
 * The format and every rule on a row are in lib/cambridgeRegistration.ts.
 *
 * The file holds ID numbers and dates of birth of children, so the screen and
 * its document are the exams office's alone, and each download is audited.
 * ------------------------------------------------------------------
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, ListOrdered, Save, Undo2 } from 'lucide-react';
import { db, setDoc } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { sortClasses } from '@/lib/classSort';
import { SCHOOL } from '@/lib/school.config';
import {
  buildRows, CAMBRIDGE_HEADINGS, candidateCsv, englishFirstFrom, nextSitting, numberInOrder, sittingId, sittingTitle,
  type CandidateRow, type EnglishFirst, type ExamRegistration as Registration, type RegistrationEntry, type SittingMonth,
} from '@/lib/cambridgeRegistration';
import {
  AccentPanel, Page, PageHeader, SoftCard, StatChip, StatStrip, btnGhost, btnPrimary, glassFieldClass,
} from '@/components/ui/design';

const COLLECTION = 'reportExamRegistrations';

type Draft = Pick<Registration, 'classIds' | 'excluded' | 'candidates'>;
const EMPTY: Draft = { classIds: [], excluded: [], candidates: {} };

const cellAt = (r: CandidateRow, heading: (typeof CAMBRIDGE_HEADINGS)[number]) => r.cells[CAMBRIDGE_HEADINGS.indexOf(heading)];
const smallField = glassFieldClass('!px-2 !py-1 !border');

function downloadFile(filename: string, text: string) {
  /* No byte-order mark, unlike lib/tableExport: this one is read by Direct. */
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ExamRegistration() {
  const { permissions, userData } = useAuth();
  const { activeStudents, classes } = useData();

  const start = useMemo(() => nextSitting(new Date()), []);
  const [month, setMonth] = useState<SittingMonth>(start.month);
  const [year, setYear] = useState(start.year);
  const sitting = sittingId(month, year);

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saved, setSaved] = useState<Draft>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    setNote('');
    getDoc(doc(db, COLLECTION, sitting)).then((snap) => {
      if (!live) return;
      const x = snap.data() as Partial<Registration> | undefined;
      const loaded: Draft = { classIds: x?.classIds ?? [], excluded: x?.excluded ?? [], candidates: x?.candidates ?? {} };
      setDraft(loaded);
      setSaved(loaded);
    }).catch((e: Error) => {
      if (live) { setDraft(EMPTY); setSaved(EMPTY); setError(`Could not read this sitting: ${e.message}`); }
    }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [sitting]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);

  const sortedClasses = useMemo(() => sortClasses(classes), [classes]);
  const classNameOf = useMemo(() => new Map(classes.map((c) => [c.id, c.name])), [classes]);

  /* Class order, then the name on the record: the order numbers are given in. */
  const pupils = useMemo(() => {
    const rank = new Map(sortedClasses.map((c, i) => [c.id, i]));
    const ticked = new Set(draft.classIds);
    return activeStudents
      .filter((s) => ticked.has(s.currentClass))
      .sort((a, b) => (rank.get(a.currentClass) ?? 0) - (rank.get(b.currentClass) ?? 0) || a.fullName.localeCompare(b.fullName));
  }, [activeStudents, draft.classIds, sortedClasses]);

  const excluded = useMemo(() => new Set(draft.excluded), [draft.excluded]);
  const entered = useMemo(() => pupils.filter((p) => !excluded.has(p.id)), [pupils, excluded]);
  const rows = useMemo(() => buildRows(entered, draft.candidates), [entered, draft.candidates]);
  const rowOf = useMemo(() => new Map(rows.map((r) => [r.studentId, r])), [rows]);
  const held = rows.filter((r) => r.holds.length);
  const ready = rows.length - held.length;
  const withWarnings = rows.filter((r) => r.warnings.length).length;
  const noDob = rows.filter((r) => !cellAt(r, 'Date of Birth')).length;
  const noId = rows.filter((r) => !cellAt(r, 'National ID')).length;

  const setEntry = useCallback((id: string, patch: Partial<RegistrationEntry>) => {
    setDraft((d) => ({ ...d, candidates: { ...d.candidates, [id]: { ...d.candidates[id], ...patch } } }));
  }, []);

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  async function save(): Promise<boolean> {
    setBusy(true);
    setError('');
    try {
      /* Empty answers come out, so the document holds only what was typed. */
      const candidates: Record<string, RegistrationEntry> = {};
      for (const [id, entry] of Object.entries(draft.candidates)) {
        const kept = Object.fromEntries(Object.entries(entry).filter(([, v]) => typeof v === 'string' && v.trim()));
        if (Object.keys(kept).length) candidates[id] = kept;
      }
      const clean: Draft = { ...draft, candidates };
      const body: Registration = { ...clean, sitting, updatedBy: userData?.uid ?? '', updatedAt: new Date().toISOString() };
      await setDoc(doc(db, COLLECTION, sitting), body);
      setDraft(clean);
      setSaved(clean);
      setNote('Saved.');
      return true;
    } catch (e) {
      setError(`Could not save: ${(e as Error).message}`);
      return false;
    } finally {
      setBusy(false);
    }
  }

  /* Saved first: numbers in a file but not here would be given out twice. */
  async function download() {
    if (dirty && !(await save())) return;
    const name = `cambridge-candidates-${SCHOOL.cambridgeCentre ? `${SCHOOL.cambridgeCentre}-` : ''}${sitting}.csv`;
    downloadFile(name, candidateCsv(rows));
    auditFromContext(userData, 'exam_registration_downloaded', 'academic', {
      targetId: sitting,
      targetType: 'setting',
      details: { sitting, candidates: ready, heldBack: held.length, classIds: draft.classIds },
    });
    setNote(`Downloaded ${ready} candidate${ready === 1 ? '' : 's'}.`);
  }

  if (!permissions.canManageExamTimetables) {
    return (
      <Page wide>
        <PageHeader icon={FileSpreadsheet} eyebrow="Exams" title="Registration" subtitle="Kept by the exam officer" />
        <SoftCard><p className="text-sm text-[var(--sp-body)]">The Cambridge candidate list holds pupils’ ID numbers and dates of birth, so it is kept by the exam officer.</p></SoftCard>
      </Page>
    );
  }

  const years = [start.year - 1, start.year, start.year + 1, start.year + 2];

  return (
    <Page wide>
      <PageHeader
        icon={FileSpreadsheet}
        eyebrow="Exams"
        title="Registration"
        subtitle={`${sittingTitle(sitting)} · the candidate file for Cambridge Direct`}
        actions={(
          <div className="flex flex-wrap gap-2">
            <select aria-label="Month" value={month} onChange={(e) => setMonth(e.target.value as SittingMonth)} disabled={dirty} className={glassFieldClass('!w-auto')}>
              <option value="June">June</option>
              <option value="November">November</option>
            </select>
            <select aria-label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} disabled={dirty} className={glassFieldClass('!w-auto')}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            {dirty && (
              <button type="button" className={btnGhost} onClick={() => setDraft(saved)} disabled={busy}><Undo2 size={15} /> Discard</button>
            )}
            <button type="button" className={btnGhost} onClick={() => void save()} disabled={busy || !dirty}><Save size={15} /> Save</button>
            <button type="button" className={btnPrimary} onClick={() => void download()} disabled={busy || ready === 0}>
              <Download size={15} /> Download {ready} candidate{ready === 1 ? '' : 's'}{held.length ? `, ${held.length} held back` : ''}
            </button>
          </div>
        )}
      />

      {error && <AccentPanel tone="rose" className="mb-4"><p className="text-sm">{error}</p></AccentPanel>}
      {note && !error && <p className="mb-3 text-sm text-[var(--sp-muted)]" role="status">{note}</p>}

      <AccentPanel tone="amber" className="mb-5">
        <ul className="list-disc space-y-1 pl-4 text-sm text-[var(--sp-body)]">
          <li><b>Upload the file as downloaded.</b> Opening and saving it in Excel strips the leading zeros from candidate numbers and can change the dates.</li>
          <li><b>Subjects are not in this file.</b> Cambridge asks for them afterwards, on Direct’s Prepare Entries; <a href="#/examTools" className="font-semibold text-primary hover:underline">Class subject lists</a> has each pupil’s.</li>
          <li><b>Check the headings once</b> against the template on Direct’s Add Candidates from Spreadsheet screen, before the first upload. They and the S for a school candidate come from Cambridge’s printed guide.</li>
          <li>Candidate numbers cannot be changed after the entry is submitted, and names and dates of birth print on the certificate as they are here.</li>
          {!SCHOOL.cambridgeCentre && <li>The school’s Cambridge centre number is not in the portal yet. The file does not need it.</li>}
        </ul>
      </AccentPanel>

      <SoftCard className="mb-5">
        <h3 className="mb-2 text-sm font-semibold text-ink dark:text-slate-100">Classes sitting {sittingTitle(sitting)}</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {sortedClasses.map((c) => (
            <label key={c.id} className="inline-flex items-center gap-1.5 text-sm text-[var(--sp-body)]">
              <input type="checkbox" checked={draft.classIds.includes(c.id)} disabled={loading}
                onChange={() => setDraft((d) => ({ ...d, classIds: toggle(d.classIds, c.id) }))} />
              {c.name}
            </label>
          ))}
        </div>
      </SoftCard>

      {pupils.length > 0 && (
        <>
          <StatStrip>
            <StatChip tone="emerald" value={ready} label="ready for the file" icon={CheckCircle2} />
            <StatChip tone={held.length ? 'rose' : 'slate'} value={held.length} label="held back" icon={AlertTriangle} />
            <StatChip tone={noDob ? 'amber' : 'slate'} value={noDob} label="no date of birth" />
            <StatChip tone="slate" value={noId} label="no ID number" />
          </StatStrip>

          <SoftCard pad="p-0">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4">
              <p className="text-sm text-[var(--sp-muted)]">
                {rows.length} of {pupils.length} pupils entered{withWarnings ? ` · ${withWarnings} with something to check` : ''}. Fix a date of birth, gender or ID on the pupil’s profile.
              </p>
              <button type="button" className={btnGhost}
                onClick={() => setDraft((d) => ({ ...d, candidates: numberInOrder(entered.map((p) => p.id), d.candidates) }))}>
                <ListOrdered size={15} /> Number in order
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-sm">
                <thead>
                  <tr className="border-y border-[var(--sp-line)] text-left text-xs text-[var(--sp-muted)]">
                    <th className="px-3 py-2 font-semibold">Enter</th>
                    <th className="px-2 py-2 font-semibold">Number</th>
                    <th className="px-2 py-2 font-semibold">Name on the certificate</th>
                    <th className="px-2 py-2 font-semibold">Gender</th>
                    <th className="px-2 py-2 font-semibold">Born</th>
                    <th className="px-2 py-2 font-semibold">ID</th>
                    <th className="px-2 py-2 font-semibold">English first?</th>
                    <th className="px-2 py-2 font-semibold">Previous centre</th>
                    <th className="px-2 py-2 font-semibold">Previous number</th>
                    <th className="px-3 py-2 font-semibold">To check</th>
                  </tr>
                </thead>
                <tbody>
                  {pupils.map((p) => {
                    const row = rowOf.get(p.id);
                    const entry = draft.candidates[p.id];
                    return (
                      <tr key={p.id} className={`border-b border-[var(--sp-line)] align-top ${row?.holds.length ? 'bg-rose-50 dark:bg-rose-950/30' : ''} ${row ? '' : 'opacity-50'}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" aria-label={`Enter ${p.fullName}`} checked={!!row}
                            onChange={() => setDraft((d) => ({ ...d, excluded: toggle(d.excluded, p.id) }))} />
                        </td>
                        <td className="px-2 py-2 w-20">
                          <input className={smallField} aria-label={`Candidate number, ${p.fullName}`} inputMode="numeric" maxLength={5} value={entry?.number ?? ''} disabled={!row}
                            onChange={(e) => setEntry(p.id, { number: e.target.value })} />
                        </td>
                        <td className="px-2 py-2 min-w-[240px]">
                          <input className={smallField} aria-label={`Name on the certificate, ${p.fullName}`} value={entry?.name ?? ''} disabled={!row}
                            placeholder={row ? cellAt(row, 'Name') : p.fullName}
                            onChange={(e) => setEntry(p.id, { name: e.target.value })} />
                          <a href={`#/studentProfile/${p.id}`} className="mt-1 block text-xs text-primary hover:underline">
                            {p.fullName} · {classNameOf.get(p.currentClass) ?? ''}
                          </a>
                        </td>
                        <td className="px-2 py-2">{row ? cellAt(row, 'Gender') || '–' : ''}</td>
                        <td className="px-2 py-2 whitespace-nowrap">{row ? cellAt(row, 'Date of Birth') || '–' : ''}</td>
                        <td className="px-2 py-2">{row ? cellAt(row, 'National ID') || '–' : ''}</td>
                        <td className="px-2 py-2">
                          <select className={smallField} aria-label={`English first language, ${p.fullName}`} disabled={!row} value={entry?.firstLanguageEnglish ?? ''}
                            onChange={(e) => setEntry(p.id, { firstLanguageEnglish: (e.target.value || undefined) as EnglishFirst | undefined })}>
                            <option value="">Record: {englishFirstFrom(p.admissions?.homeLanguage) || '–'}</option>
                            <option value="Y">Y</option>
                            <option value="N">N</option>
                          </select>
                        </td>
                        <td className="px-2 py-2 w-24">
                          <input className={smallField} aria-label={`Previous centre, ${p.fullName}`} value={entry?.previousCentre ?? ''} disabled={!row}
                            onChange={(e) => setEntry(p.id, { previousCentre: e.target.value })} />
                        </td>
                        <td className="px-2 py-2 w-20">
                          <input className={smallField} aria-label={`Previous candidate number, ${p.fullName}`} value={entry?.previousCandidate ?? ''} disabled={!row}
                            onChange={(e) => setEntry(p.id, { previousCandidate: e.target.value })} />
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {row?.holds.map((h) => <div key={h} className="font-semibold text-rose-700 dark:text-rose-300">{h}</div>)}
                          {row?.warnings.map((w) => <div key={w} className="text-[var(--sp-muted)]">{w}</div>)}
                          {!row && <span className="text-[var(--sp-muted)]">Not entered</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SoftCard>
        </>
      )}

      {!loading && pupils.length === 0 && (
        <SoftCard><p className="text-sm text-[var(--sp-muted)]">Tick the classes sitting this series to see their pupils.</p></SoftCard>
      )}
    </Page>
  );
}
